import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  CopyAll as CopyAllIcon,
} from '@mui/icons-material';

const ApiDocumentationTab = ({credential, pdfLoading, onDownloadPdf}) => {
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const [copiedText, setCopiedText] = useState('');

  const copyCurlCommand = async () => {
    const curlCommand = `curl -X POST "https://be.blackdawn.info/api/v1/external/data-entry" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${credential.api_key}" \\
  -H "X-Username: ${credential.username || 'your_username'}" \\
  -H "X-Password: ${credential.password || 'your_password'}" \\
  -H "User-Agent: ${credential.credential_name}/1.0" \\
  -d '{
    "fullName": "Jane Smith",
    "email": "jane.smith@example.com",
    "phone": "19876543210",
    "age": "28",
    "country": "Canada",
    "marks": "Lead score: 92, Campaign: CAMP_2024_Q1",
    "MPC_4": "Very High interest level",
    "other_data": {
      "lead_score": 92,
      "campaign_id": "CAMP_2024_Q1",
      "interest_level": "Very High",
      "integration_source": "${credential.credential_name}",
      "credential_name": "${credential.credential_name}"
    },
    "data_source": "external_api"
  }'`;

    try {
      await navigator.clipboard.writeText(curlCommand);
      // Show success message
      const button = document.querySelector(
        'button[onclick="copyCurlCommand()"]',
      );
      if (button) {
        const originalText = button.textContent;
        button.textContent = '✅ Copied!';
        button.style.background = '#4caf50';
        setTimeout(() => {
          button.textContent = originalText;
          button.style.background = '#1976d2';
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
      alert('Failed to copy cURL command');
    }
  };

  // Make copyCurlCommand available globally for onclick handler
  useEffect(() => {
    window.copyCurlCommand = copyCurlCommand;
    return () => {
      delete window.copyCurlCommand;
    };
  }, [credential]);

  return (
    <Card>
      <CardContent>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={2}
        >
          <Box>
            <Typography variant='h6' gutterBottom>
              API Documentation
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              Use this API credential to send data to our system. Choose the
              appropriate documentation for your integration needs.
            </Typography>
          </Box>
          <Box display='flex' gap={2}>
            <Button
              variant='contained'
              startIcon={<PdfIcon />}
              onClick={() => onDownloadPdf('single')}
              disabled={pdfLoading}
              color='success'
              sx={{minWidth: 180}}
            >
              {pdfLoading ? 'Generating...' : '📄 Single Lead PDF'}
            </Button>
            <Button
              variant='contained'
              startIcon={<PdfIcon />}
              onClick={() => onDownloadPdf('bulk')}
              disabled={pdfLoading}
              color='primary'
              sx={{minWidth: 180}}
            >
              {pdfLoading ? 'Generating...' : '📦 Bulk Lead PDF'}
            </Button>
          </Box>
        </Box>

        {/* API Endpoints Information */}
        <Box mb={4}>
          <Typography variant='h6' gutterBottom color='primary'>
            📡 API Endpoints
          </Typography>

          {/* Single Lead Endpoint */}
          <Card
            variant='outlined'
            sx={{p: 2, mb: 2, backgroundColor: 'grey.50'}}
          >
            <Typography variant='subtitle1' gutterBottom color='success.dark'>
              <strong>🔄 Single Lead Entry</strong>
            </Typography>
            <Typography variant='body1' fontFamily='monospace'>
              <strong>POST</strong>{' '}
              https://be.blackdawn.info/api/v1/external/data-entry
            </Typography>
            <Typography variant='body2' color='textSecondary' sx={{mt: 1}}>
              <strong>Use for:</strong> Sending one lead at a time. Perfect for
              real-time integrations, webhooks, and form submissions.
            </Typography>
          </Card>

          {/* Bulk Lead Endpoint */}
          <Card variant='outlined' sx={{p: 2, backgroundColor: 'grey.50'}}>
            <Typography variant='subtitle1' gutterBottom color='primary.dark'>
              <strong>📦 Bulk Lead Entry</strong>
            </Typography>
            <Typography variant='body1' fontFamily='monospace'>
              <strong>POST</strong>{' '}
              https://be.blackdawn.info/api/v1/external/bulk-data-entry
            </Typography>
            <Typography variant='body2' color='textSecondary' sx={{mt: 1}}>
              <strong>Use for:</strong> Sending multiple leads in one request.
              Perfect for batch imports, CSV uploads, and high-volume
              integrations.
              <strong>Performance:</strong> 5-10x faster than individual
              requests.
            </Typography>
            <Alert severity='info' sx={{mt: 1}}>
              <strong>Rate Limits:</strong> Max 1000 leads per request, 10
              requests per minute per API key. Multiple systems can send data
              simultaneously without conflicts.
            </Alert>
          </Card>
        </Box>

        {/* Headers */}
        <Box mb={4}>
          <Typography variant='h6' gutterBottom color='primary'>
            🔐 Headers
          </Typography>
          <Card variant='outlined' sx={{p: 2, backgroundColor: 'grey.50'}}>
            <Typography variant='body2' fontFamily='monospace'>
              Content-Type: application/json
              <br />
              X-API-Key: {credential.api_key}
              <br />
              X-Username: {credential.username || 'your_username'}
              <br />
              X-Password: {credential.password || 'your_password'}
              <br />
              User-Agent: YourApp/1.0
            </Typography>
            <Typography variant='body2' color='textSecondary' sx={{mt: 1}}>
              <strong>Security:</strong> Your API key identifies your
              integration and grants access. Keep it secure and never expose it
              in client-side code.
            </Typography>
            <Typography variant='body2' color='textSecondary' sx={{mt: 1}}>
              <strong>Authentication:</strong> The API uses API key
              authentication via the X-API-Key header. Username and password
              fields are optional and can be used for additional authentication
              or identification purposes within your application.
            </Typography>
            <Typography variant='body2' color='textSecondary' sx={{mt: 1}}>
              <strong>Best Practices:</strong>
            </Typography>
            <ul style={{marginTop: 8, paddingLeft: 20}}>
              <li>Store your API key securely in environment variables</li>
              <li>Use HTTPS for all API requests</li>
              <li>Never log or expose API keys in client-side code</li>
              <li>Rotate API keys regularly for enhanced security</li>
              <li>
                Use strong, unique passwords if implementing username/password
                authentication
              </li>
            </ul>
          </Card>
        </Box>

        {/* Request Body Structure */}
        <Box mb={4}>
          <Typography variant='h6' gutterBottom color='primary'>
            📋 Request Body Structure
          </Typography>

          {/* Single Lead Structure */}
          <Box mb={3}>
            <Typography variant='subtitle1' gutterBottom color='success.dark'>
              <strong>🔄 Single Lead Request</strong>
            </Typography>
            <Card variant='outlined' sx={{p: 2, backgroundColor: 'grey.50'}}>
              <Typography
                variant='body2'
                fontFamily='monospace'
                component='pre'
                sx={{whiteSpace: 'pre-wrap'}}
              >
                {`{
  "fullName": "John Doe",
  "email": "john.doe@example.com",
  "phone": "12345678900",
  "age": "30",
  "country": "United States",
  "marks": "Lead score: 85, Campaign: CAMP_001",
  "MPC_4": "High interest level",
  "other_data": {
    "lead_score": 85,
    "campaign_id": "CAMP_001",
    "interest_level": "High",
    "integration_source": "${credential.credential_name}",
    "credential_name": "${credential.credential_name}"
  },
  "data_source": "external_api"
}`}
              </Typography>
            </Card>
          </Box>

          {/* Bulk Lead Structure */}
          <Box mb={3}>
            <Typography variant='subtitle1' gutterBottom color='primary.dark'>
              <strong>📦 Bulk Lead Request</strong>
            </Typography>
            <Card variant='outlined' sx={{p: 2, backgroundColor: 'grey.50'}}>
              <Typography
                variant='body2'
                fontFamily='monospace'
                component='pre'
                sx={{whiteSpace: 'pre-wrap'}}
              >
                {`{
  "leads": [
    {
      "fullName": "John Doe",
      "email": "john.doe@example.com",
      "phone": "12345678900",
      "age": "30",
      "country": "United States",
      "marks": "Lead score: 85, Campaign: CAMP_001",
      "MPC_4": "High interest level",
      "other_data": {
        "lead_score": 85,
        "campaign_id": "CAMP_001",
        "interest_level": "High"
      }
    },
    {
      "fullName": "Jane Smith",
      "email": "jane.smith@example.com",
      "phone": "19876543210",
      "age": "28",
      "country": "Canada",
      "marks": "Lead score: 92, Campaign: CAMP_002",
      "MPC_4": "Very High interest level",
      "other_data": {
        "lead_score": 92,
        "campaign_id": "CAMP_002",
        "interest_level": "Very High"
      }
    }
  ],
  "batch_size": 50,
  "validate_all_first": false
}`}
              </Typography>
              <Typography variant='body2' color='textSecondary' sx={{mt: 1}}>
                <strong>Optional Parameters:</strong>
                <br />• <code>batch_size</code>: Process leads in batches
                (default: 50, max: 1000)
                <br />• <code>validate_all_first</code>: Validate all leads
                before processing any (default: false)
              </Typography>
            </Card>
          </Box>
        </Box>

        {/* Field Descriptions */}
        <Box mb={4}>
          <Typography variant='h6' gutterBottom color='primary'>
            📝 Field Descriptions
          </Typography>
          <Alert severity='info' sx={{mb: 2}}>
            <strong>Note:</strong> <code>fullName</code>, <code>email</code>,{' '}
            <code>phone</code>, <code>age</code>, <code>country</code>,{' '}
            <code>marks</code>, and <code>MPC_4</code> are all required fields.
            Only <code>other_data</code> is optional.
          </Alert>
          <Alert severity='warning' sx={{mb: 2}}>
            <strong>Authentication Fields:</strong> The <code>username</code>{' '}
            and <code>password</code> are sent via <code>X-Username</code> and{' '}
            <code>X-Password</code> headers for:
            <ul style={{marginTop: 8, paddingLeft: 20}}>
              <li>Additional user identification within your system</li>
              <li>Integration with third-party authentication systems</li>
              <li>Custom user management and tracking</li>
              <li>Audit trails and user attribution</li>
            </ul>
          </Alert>
          <TableContainer component={Paper} variant='outlined'>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Field</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Type</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Required</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Description</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>fullName</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>Full name of the contact</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>email</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>Email address (must be valid format)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>phone</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>
                    Phone number (7-15 digits, start with 1-9, no + prefix
                    allowed)
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>age</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>Age of the contact (stored as string)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>country</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>Country name</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>marks</TableCell>
                  <TableCell>text</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>
                    Additional text data or notes about the contact
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>MPC_4</TableCell>
                  <TableCell>text</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>Additional text data or classification</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>other_data</TableCell>
                  <TableCell>object</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>
                    Custom data as JSON object (optional). Can include custom
                    fields for additional data storage
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>data_source</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>
                    Source identifier for the data (defaults to
                    &apos;external_api&apos;)
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Example cURL Requests */}
        <Box mb={4}>
          <Typography variant='h6' gutterBottom color='primary'>
            💻 Example cURL Requests
          </Typography>
          <Alert severity='info' sx={{mb: 2}}>
            <strong>Postman Users:</strong> Copy these cURL commands and paste
            them directly into Postman&apos;s &quot;Import&quot; feature. The
            body will be automatically parsed and formatted for you.
          </Alert>

          {/* Single Lead cURL */}
          <Box mb={3}>
            <Typography variant='subtitle1' gutterBottom color='success.dark'>
              <strong>🔄 Single Lead cURL</strong>
            </Typography>
            <Card
              variant='outlined'
              sx={{p: 2, backgroundColor: 'grey.50', position: 'relative'}}
            >
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                mb={1}
              >
                <Typography variant='subtitle2' color='textSecondary'>
                  Send one lead
                </Typography>
                <Tooltip title='Copy single lead cURL'>
                  <IconButton
                    size='small'
                    onClick={copyCurlCommand}
                    color='primary'
                  >
                    <CopyAllIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography
                variant='body2'
                fontFamily='monospace'
                component='pre'
                sx={{whiteSpace: 'pre-wrap', fontSize: '0.875rem'}}
              >
                {`curl -X POST "https://be.blackdawn.info/api/v1/external/data-entry" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${credential.api_key}" \\
  -H "X-Username: ${credential.username || 'your_username'}" \\
  -H "X-Password: ${credential.password || 'your_password'}" \\
  -d '{
    "fullName": "Jane Smith",
    "email": "jane.smith@example.com",
    "phone": "19876543210",
    "age": "28",
    "country": "Canada",
    "marks": "Lead score: 92, Campaign: CAMP_2024_Q1",
    "MPC_4": "Very High interest level",
    "other_data": {
      "lead_score": 92,
      "campaign_id": "CAMP_2024_Q1",
      "interest_level": "Very High"
    },
    "data_source": "external_api"
  }'`}
              </Typography>
            </Card>
          </Box>

          {/* Bulk Lead cURL */}
          <Box mb={3}>
            <Typography variant='subtitle1' gutterBottom color='primary.dark'>
              <strong>📦 Bulk Lead cURL</strong>
            </Typography>
            <Card
              variant='outlined'
              sx={{p: 2, backgroundColor: 'grey.50', position: 'relative'}}
            >
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                mb={1}
              >
                <Typography variant='subtitle2' color='textSecondary'>
                  Send multiple leads (recommended for 10+ leads)
                </Typography>
                <Tooltip title='Copy bulk lead cURL'>
                  <IconButton
                    size='small'
                    onClick={() => {
                      const bulkCurlCommand = `curl -X POST "https://be.blackdawn.info/api/v1/external/bulk-data-entry" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${credential.api_key}" \\
  -H "X-Username: ${credential.username || 'your_username'}" \\
  -H "X-Password: ${credential.password || 'your_password'}" \\
  -d '{
    "leads": [
      {
        "fullName": "John Doe",
        "email": "john.doe@example.com",
        "phone": "12345678900",
        "age": "30",
        "country": "United States",
        "marks": "Lead score: 85, Campaign: CAMP_001",
        "MPC_4": "High interest level",
        "other_data": {
          "lead_score": 85,
          "campaign_id": "CAMP_001",
          "interest_level": "High"
        }
      },
      {
        "fullName": "Jane Smith",
        "email": "jane.smith@example.com",
        "phone": "19876543210",
        "age": "28",
        "country": "Canada",
        "marks": "Lead score: 92, Campaign: CAMP_002",
        "MPC_4": "Very High interest level",
        "other_data": {
          "lead_score": 92,
          "campaign_id": "CAMP_002",
          "interest_level": "Very High"
        }
      }
    ],
    "batch_size": 50,
    "validate_all_first": false
  }'`;
                      navigator.clipboard.writeText(bulkCurlCommand);
                    }}
                    color='primary'
                  >
                    <CopyAllIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography
                variant='body2'
                fontFamily='monospace'
                component='pre'
                sx={{whiteSpace: 'pre-wrap', fontSize: '0.875rem'}}
              >
                {`curl -X POST "https://be.blackdawn.info/api/v1/external/bulk-data-entry" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${credential.api_key}" \\
  -H "X-Username: ${credential.username || 'your_username'}" \\
  -H "X-Password: ${credential.password || 'your_password'}" \\
  -d '{
    "leads": [
      {
        "fullName": "John Doe",
        "email": "john.doe@example.com",
        "phone": "12345678900",
        "age": "30",
        "country": "United States",
        "marks": "Lead score: 85, Campaign: CAMP_001",
        "MPC_4": "High interest level",
        "other_data": {
          "lead_score": 85,
          "campaign_id": "CAMP_001",
          "interest_level": "High"
        }
      },
      {
        "fullName": "Jane Smith",
        "email": "jane.smith@example.com",
        "phone": "19876543210",
        "age": "28",
        "country": "Canada",
        "marks": "Lead score: 92, Campaign: CAMP_002",
        "MPC_4": "Very High interest level",
        "other_data": {
          "lead_score": 92,
          "campaign_id": "CAMP_002",
          "interest_level": "Very High"
        }
      }
    ],
    "batch_size": 50,
    "validate_all_first": false
  }'`}
              </Typography>
            </Card>
          </Box>
        </Box>

        {/* Response Format */}
        <Box mb={4}>
          <Typography variant='h6' gutterBottom color='primary'>
            📤 Response Format
          </Typography>

          {/* Single Lead Response */}
          <Box mb={3}>
            <Typography variant='subtitle1' gutterBottom color='success.dark'>
              <strong>🔄 Single Lead Response</strong>
            </Typography>
            <Card variant='outlined' sx={{p: 2, backgroundColor: 'grey.50'}}>
              <Typography
                variant='body2'
                fontFamily='monospace'
                component='pre'
                sx={{whiteSpace: 'pre-wrap'}}
              >
                {`{
  "data": {
    "id": 12345,
    "valid": true
  },
  "message": "Data processed successfully",
  "status": 201
}`}
              </Typography>
            </Card>
          </Box>

          {/* Bulk Lead Response */}
          <Box mb={3}>
            <Typography variant='subtitle1' gutterBottom color='primary.dark'>
              <strong>📦 Bulk Lead Response</strong>
            </Typography>
            <Card variant='outlined' sx={{p: 2, backgroundColor: 'grey.50'}}>
              <Typography
                variant='body2'
                fontFamily='monospace'
                component='pre'
                sx={{whiteSpace: 'pre-wrap'}}
              >
                {`{
  "success": true,
  "summary": {
    "total_received": 100,
    "successful": 85,
    "failed": 15,
    "processing_time_ms": 1250
  },
  "results": [
    { "lead_index": 0, "success": true, "id": 12345 },
    { "lead_index": 1, "success": false, "errors": ["Invalid email format"] },
    { "lead_index": 2, "success": true, "id": 12346 }
  ],
  "failed_leads": [
    {
      "lead_index": 1,
      "data": { "fullName": "Invalid User", "email": "invalid-email" },
      "errors": ["Invalid email format"]
    }
  ],
  "message": "Bulk processing completed",
  "status": 200
}`}
              </Typography>
              <Typography variant='body2' color='textSecondary' sx={{mt: 1}}>
                <strong>Response Fields:</strong>
                <br />• <code>summary</code>: Overall statistics of the bulk
                operation
                <br />• <code>results</code>: Individual result for each lead
                (success/failure + ID or errors)
                <br />• <code>failed_leads</code>: Only the leads that failed
                validation (for easy retry)
              </Typography>
            </Card>
          </Box>
        </Box>

        {/* Error Response */}
        <Box mb={4}>
          <Typography variant='h6' gutterBottom color='primary'>
            ❌ Error Response Format
          </Typography>
          <Card variant='outlined' sx={{p: 2, backgroundColor: 'grey.50'}}>
            <Typography
              variant='body2'
              fontFamily='monospace'
              component='pre'
              sx={{whiteSpace: 'pre-wrap'}}
            >
              {`{
  "data": {
    "valid": false,
    "errors": ["Invalid email format"]
  },
  "message": "Invalid data",
  "status": 400
}`}
            </Typography>
          </Card>
        </Box>

        {/* Security & Authentication */}
        <Box mb={4}>
          <Typography variant='h6' gutterBottom color='primary'>
            🔒 Security & Authentication
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                variant='outlined'
                sx={{p: 2, backgroundColor: 'success.50'}}
              >
                <Typography
                  variant='subtitle1'
                  gutterBottom
                  color='success.dark'
                >
                  <strong>✅ Security Best Practices</strong>
                </Typography>
                <ul style={{margin: 0, paddingLeft: 20}}>
                  <li>Store API keys in environment variables</li>
                  <li>Use HTTPS for all API communications</li>
                  <li>Implement proper error handling</li>
                  <li>Log API usage for monitoring</li>
                  <li>Use strong, unique passwords</li>
                  <li>Rotate API keys regularly</li>
                </ul>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant='outlined' sx={{p: 2, backgroundColor: 'error.50'}}>
                <Typography variant='subtitle1' gutterBottom color='error.dark'>
                  <strong>❌ Security Don&apos;ts</strong>
                </Typography>
                <ul style={{margin: 0, paddingLeft: 20}}>
                  <li>Expose API keys in client-side code</li>
                  <li>Log API keys in application logs</li>
                  <li>Use weak or default passwords</li>
                  <li>Send sensitive data over HTTP</li>
                  <li>Share API keys between environments</li>
                  <li>Store credentials in plain text</li>
                </ul>
              </Card>
            </Grid>
          </Grid>

          <Card
            variant='outlined'
            sx={{p: 2, mt: 2, backgroundColor: 'info.50'}}
          >
            <Typography variant='subtitle1' gutterBottom color='info.dark'>
              <strong>🔐 Username & Password Usage</strong>
            </Typography>
            <Typography variant='body2' paragraph>
              The <code>username</code> and <code>password</code> fields serve
              multiple purposes:
            </Typography>
            <ul style={{margin: 0, paddingLeft: 20}}>
              <li>
                <strong>User Identification:</strong> Track which user or system
                submitted the data
              </li>
              <li>
                <strong>Integration Context:</strong> Provide context for data
                processing and routing
              </li>
              <li>
                <strong>Audit Trail:</strong> Enable comprehensive logging and
                tracking
              </li>
              <li>
                <strong>Custom Authentication:</strong> Support additional
                authentication layers
              </li>
            </ul>
            <Typography variant='body2' sx={{mt: 2}}>
              <strong>Data Privacy:</strong> All data submitted through this API
              is processed securely and stored in compliance with data
              protection standards. Username and password fields are encrypted
              at rest and transmitted securely.
            </Typography>
          </Card>
        </Box>

        {/* When to Use Single vs Bulk */}
        <Box mb={4}>
          <Typography variant='h6' gutterBottom color='primary'>
            🤔 When to Use Single vs Bulk
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card
                variant='outlined'
                sx={{p: 2, backgroundColor: 'success.50'}}
              >
                <Typography
                  variant='subtitle1'
                  gutterBottom
                  color='success.dark'
                >
                  <strong>🔄 Use Single Endpoint When:</strong>
                </Typography>
                <ul style={{margin: 0, paddingLeft: 20}}>
                  <li>
                    <strong>Real-time integrations:</strong> Webhooks, form
                    submissions
                  </li>
                  <li>
                    <strong>Small volumes:</strong> 1-10 leads at a time
                  </li>
                  <li>
                    <strong>Immediate feedback:</strong> Need instant
                    success/failure response
                  </li>
                  <li>
                    <strong>Simple integrations:</strong> Zapier, web forms,
                    chatbots
                  </li>
                  <li>
                    <strong>Testing:</strong> Development and debugging
                  </li>
                </ul>
                <Typography variant='body2' color='textSecondary' sx={{mt: 1}}>
                  <strong>Performance:</strong> ~100ms per request
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                variant='outlined'
                sx={{p: 2, backgroundColor: 'primary.50'}}
              >
                <Typography
                  variant='subtitle1'
                  gutterBottom
                  color='primary.dark'
                >
                  <strong>📦 Use Bulk Endpoint When:</strong>
                </Typography>
                <ul style={{margin: 0, paddingLeft: 20}}>
                  <li>
                    <strong>Batch imports:</strong> CSV uploads, data migrations
                  </li>
                  <li>
                    <strong>High volumes:</strong> 10+ leads at a time
                  </li>
                  <li>
                    <strong>Performance critical:</strong> Need fastest
                    processing
                  </li>
                  <li>
                    <strong>Multiple systems:</strong> Concurrent data from
                    different sources
                  </li>
                  <li>
                    <strong>Production workloads:</strong> Regular bulk
                    operations
                  </li>
                </ul>
                <Typography variant='body2' color='textSecondary' sx={{mt: 1}}>
                  <strong>Performance:</strong> ~10ms per lead (5-10x faster)
                </Typography>
              </Card>
            </Grid>
          </Grid>

          <Alert severity='info' sx={{mt: 2}}>
            <Typography variant='body2'>
              <strong>💡 Pro Tip:</strong> Start with single endpoint for
              development and testing, then switch to bulk endpoint for
              production when you need to process multiple leads. Both endpoints
              use the same authentication and field validation rules.
            </Typography>
          </Alert>
        </Box>

        {/* Integration Examples */}
        <Box mb={4}>
          <Typography variant='h6' gutterBottom color='primary'>
            🔗 Integration Examples
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card variant='outlined' sx={{p: 2}}>
                <Box
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                  mb={1}
                >
                  <Typography variant='subtitle1'>
                    <strong>JavaScript/Node.js</strong>
                  </Typography>
                  <Tooltip
                    title={
                      copiedText === 'javascript'
                        ? 'Copied!'
                        : 'Copy cURL for JavaScript/Node.js'
                    }
                    open={copyTooltipOpen && copiedText === 'javascript'}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                  >
                    <IconButton
                      size='small'
                      onClick={() => {
                        const curlCommand = `curl -X POST "https://be.blackdawn.info/api/v1/external/data-entry" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${credential.api_key}" \\
  -H "X-Username: ${credential.username || 'your_username'}" \\
  -H "X-Password: ${credential.password || 'your_password'}" \\
  -H "User-Agent: javascript/1.0" \\
  -d '{
    "fullName": "John Doe",
    "email": "john.doe@example.com",
    "phone": "12345678900",
    "age": "30",
    "country": "United States",
    "marks": "Lead score: 85, Campaign: CAMP_001",
    "MPC_4": "High interest level",
    "other_data": {
      "lead_score": 85,
      "campaign_id": "CAMP_001",
      "interest_level": "High",
      "integration_source": "javascript",
      "credential_name": "${credential.credential_name}"
    },
    "data_source": "external_api"
  }'`;
                        navigator.clipboard.writeText(curlCommand);
                        setCopiedText('javascript');
                        setCopyTooltipOpen(true);
                        setTimeout(() => setCopyTooltipOpen(false), 2000);
                      }}
                      color='primary'
                    >
                      <CopyAllIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography
                  variant='body2'
                  fontFamily='monospace'
                  component='pre'
                  sx={{whiteSpace: 'pre-wrap', fontSize: '0.8rem'}}
                >
                  {`const axios = require('axios');

const sendData = async (data) => {
  try {
    const response = await axios.post(
      'https://be.blackdawn.info/api/v1/external/data-entry',
      {
        ...data,
        other_data: {
          ...data.other_data,
          integration_source: '${credential.credential_name}',
          credential_name: '${credential.credential_name}',
          username: data.username || 'default_username',
          password: data.password || 'default_password',
          timestamp: new Date().toISOString()
        },
        data_source: 'external_api'
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': '${credential.api_key}',
          'User-Agent': '${credential.credential_name}/1.0'
        }
      }
    );
    console.log('Success:', response.data);
  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
  }
};`}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant='outlined' sx={{p: 2}}>
                <Box
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                  mb={1}
                >
                  <Typography variant='subtitle1'>
                    <strong>Python</strong>
                  </Typography>
                  <Tooltip
                    title={
                      copiedText === 'python'
                        ? 'Copied!'
                        : 'Copy cURL for Python'
                    }
                    open={copyTooltipOpen && copiedText === 'python'}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                  >
                    <IconButton
                      size='small'
                      onClick={() => {
                        const curlCommand = `curl -X POST "https://be.blackdawn.info/api/v1/external/data-entry" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${credential.api_key}" \\
  -H "X-Username: ${credential.username || 'your_username'}" \\
  -H "X-Password: ${credential.password || 'your_password'}" \\
  -H "User-Agent: python/1.0" \\
  -d '{
    "fullName": "John Doe",
    "email": "john.doe@example.com",
    "phone": "12345678900",
    "age": "30",
    "country": "United States",
    "marks": "Lead score: 85, Campaign: CAMP_001",
    "MPC_4": "High interest level",
    "other_data": {
      "lead_score": 85,
      "campaign_id": "CAMP_001",
      "interest_level": "High",
      "integration_source": "python",
      "credential_name": "${credential.credential_name}"
    },
    "data_source": "external_api"
  }'`;
                        navigator.clipboard.writeText(curlCommand);
                        setCopiedText('python');
                        setCopyTooltipOpen(true);
                        setTimeout(() => setCopyTooltipOpen(false), 2000);
                      }}
                      color='primary'
                    >
                      <CopyAllIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography
                  variant='body2'
                  fontFamily='monospace'
                  component='pre'
                  sx={{whiteSpace: 'pre-wrap', fontSize: '0.8rem'}}
                >
                  {`import requests
import json
from datetime import datetime

def send_data(data):
    url = 'https://be.blackdawn.info/api/v1/external/data-entry'
    headers = {
        'Content-Type': 'application/json',
        'X-API-Key': '${credential.api_key}',
        'User-Agent': '${credential.credential_name}/1.0'
    }
    
    # Add integration metadata and authentication fields to other_data
    data['other_data'] = {
        **data.get('other_data', {}),
        'integration_source': '${credential.credential_name}',
        'credential_name': '${credential.credential_name}',
        'username': data.get('username', 'default_username'),
        'password': data.get('password', 'default_password'),
        'timestamp': datetime.now().isoformat()
    }
    data['data_source'] = 'external_api'
    
    try:
        response = requests.post(url, json=data, headers=headers)
        print('Success:', response.json())
    except requests.exceptions.RequestException as e:
        print('Error:', e)`}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Status Information */}
        <Box>
          <Alert severity='info'>
            <Typography variant='body2'>
              <strong>Status:</strong>{' '}
              {credential.is_active ? '🟢 Active' : '🔴 Inactive'} |
              <strong> Integration:</strong> {credential.credential_name} |
              <strong> Live Period:</strong> {getLivePeriodText()}
            </Typography>
          </Alert>
        </Box>
      </CardContent>
    </Card>
  );
};

const getLivePeriodText = () => {
  // This function should be passed as a prop or moved to a utility file
  return 'Forever'; // Placeholder
};

ApiDocumentationTab.propTypes = {
  credential: PropTypes.shape({
    api_key: PropTypes.string,
    username: PropTypes.string,
    password: PropTypes.string,
    // integration_source removed - using credential_name instead
    credential_name: PropTypes.string,
    is_active: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  }).isRequired,
  pdfLoading: PropTypes.bool.isRequired,
  onDownloadPdf: PropTypes.func.isRequired,
};

export default ApiDocumentationTab;
